import { Note } from "@nexxt/app-kit";
import { TURNSTILE_HEADER } from "@nexxt/contracts/identity";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { accountApp } from "../client";
import { m } from "../paraglide/messages.js";

// Turnstile on the sign-in, the request of a reset, the reset, the acceptance of an invitation (ADR-024 point 5,
// ADR-148) and the submission of an e-TAF (ADR-175): Cloudflare's script from its own origin, the one the CSP allows
// besides ours (ADR-154), rendered by hand. Its token goes in a header of the next request, and is good for one: after
// every answer a new widget.

const SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
/** The flexible widget is at least this wide: below, the compact one, or the page would scroll at 320 px. */
const FLEXIBLE_MIN_WIDTH = 300;

interface TurnstileApi {
  render(container: HTMLElement, options: Record<string, unknown>): string;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

/** The token of the page's widget, in the header the gateway reads (ADR-148): for the client the app gives. */
export const withTurnstile = (token: string) => ({ headers: { [TURNSTILE_HEADER]: token } });

let script: Promise<TurnstileApi> | undefined;

/** The script once per page; a failed load is tried again by the next widget. */
function loadTurnstile(): Promise<TurnstileApi> {
  script ??= new Promise<TurnstileApi>((resolve, reject) => {
    const tag = document.createElement("script");
    tag.src = SCRIPT;
    tag.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error("turnstile_missing")));
    tag.onerror = () => reject(new Error("turnstile_unreachable"));
    document.head.appendChild(tag);
  }).catch((error: unknown) => {
    script = undefined;
    throw error;
  });
  return script;
}

interface TurnstileWidgetProps {
  onToken: (token: string | null) => void;
  /** What the page cannot do without the check. */
  unavailableText: string;
}

function TurnstileWidget({ onToken, unavailableText }: TurnstileWidgetProps) {
  const container = useRef<HTMLDivElement>(null);
  // the app's widget for this stage: none, and the page says what it cannot do without it
  const siteKey = accountApp().turnstileSiteKey;
  const [unavailable, setUnavailable] = useState(!siteKey);

  useEffect(() => {
    if (!siteKey) return;
    let widget: string | undefined;
    let gone = false;
    loadTurnstile().then(
      (turnstile) => {
        const element = container.current;
        if (gone || !element) return;
        widget = turnstile.render(element, {
          sitekey: siteKey,
          theme: document.documentElement.classList.contains("dark") ? "dark" : "light",
          size: element.clientWidth >= FLEXIBLE_MIN_WIDTH ? "flexible" : "compact",
          language: "en",
          callback: (token: string) => onToken(token),
          "expired-callback": () => onToken(null),
          // handled here, so the widget does not throw: it shows its own error and offers to try again
          "error-callback": () => onToken(null),
        });
      },
      () => {
        if (!gone) setUnavailable(true);
      },
    );
    return () => {
      gone = true;
      if (widget) window.turnstile?.remove(widget);
      // a widget that leaves the page takes its token: the page has to show it again before the next request
      onToken(null);
    };
  }, [onToken, siteKey]);

  if (unavailable) return <Note tone="warning">{unavailableText}</Note>;
  return <div ref={container} className="min-h-16" />;
}

/**
 * The token of the page's widget, and `renew` after every answer of the gateway: a token is spent by one request.
 * `unavailable` says what the page cannot do while the check cannot run: signing in, unless the page says otherwise.
 */
export function useTurnstile(unavailable = m.turnstile_unavailable()): {
  token: string | null;
  renew: () => void;
  widget: ReactNode;
} {
  const [token, setToken] = useState<string | null>(null);
  const [round, setRound] = useState(0);
  return {
    token,
    renew: () => {
      setToken(null);
      setRound((n) => n + 1);
    },
    widget: <TurnstileWidget key={round} onToken={setToken} unavailableText={unavailable} />,
  };
}
